import { AxiosInstance } from 'axios';
import Client from '../Client.provider';
import {
  iModules,
  iTicketingSetup,
  iTicketsPage,
  iTicketingStats,
  iScanInfo,
  iScanResult,
  iPublicTicketing,
  iPublicOrder,
  iExhibitorCandidate,
  iTicketTypeInput,
  iPromoInput,
  iRegisterResponse,
  iOfflineList,
  iCheckpoint,
  iCheckpointInput,
  iCheckpointList,
  iCheckpointScanInfo,
  iExhibitorPassList,
  iExhibitorPassPage,
} from './Ticketing.type';

function apiError(error: any): Error {
  const data = error?.response?.data;
  const firstError =
    data?.errors && Object.values(data.errors as Record<string, string[]>)[0];
  const err = new Error(
    data?.message ||
      (Array.isArray(firstError) ? firstError[0] : undefined) ||
      `${error?.response?.status ?? ''} | Erreur inconnue`
  ) as Error & { errors?: Record<string, string[]>; status?: number };
  err.errors = data?.errors;
  err.status = error?.response?.status;
  return err;
}

/** Ticketing module: admin, scan (logged in) and public (visitors) calls. */
export class TicketingRepository extends Client {
  // ---- modules of an event
  public async getModules(eventId: string | number): Promise<iModules> {
    return await this.restricted()
      .get(`/events/${eventId}/modules`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async setModule(
    eventId: string | number,
    module: string,
    isEnabled: boolean
  ): Promise<iModules> {
    return await this.restricted()
      .put(`/events/${eventId}/modules`, { module, is_enabled: isEnabled })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Signed link to bbmatch without event (its home for this user), valid one minute. */
  public async getMatchmakingHomeLink(): Promise<{ url: string }> {
    return await this.restricted()
      .get(`/modules/matchmaking/link`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Signed link to bbmatch (matchmaking module), valid one minute. */
  public async getMatchmakingLink(
    eventId: string | number
  ): Promise<{ url: string }> {
    return await this.restricted()
      .get(`/events/${eventId}/modules/matchmaking/link`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- set-up
  public async getSetup(eventId: string | number): Promise<iTicketingSetup> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async saveSetup(
    eventId: string | number,
    data: Record<string, unknown>
  ): Promise<iTicketingSetup> {
    return await this.restricted()
      .put(`/events/${eventId}/ticketing`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async uploadLogo(
    eventId: string | number,
    file: File
  ): Promise<iTicketingSetup> {
    const form = new FormData();
    form.append('logo', file);
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/logo`, form)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async deleteLogo(eventId: string | number): Promise<iTicketingSetup> {
    return await this.restricted()
      .delete(`/events/${eventId}/ticketing/logo`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Banner background or venue plan of the e-ticket. */
  public async uploadImage(
    eventId: string | number,
    kind: 'logo' | 'banner' | 'plan',
    file: File
  ): Promise<iTicketingSetup> {
    const form = new FormData();
    form.append('image', file);
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/image/${kind}`, form)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async deleteImage(
    eventId: string | number,
    kind: 'logo' | 'banner' | 'plan'
  ): Promise<iTicketingSetup> {
    return await this.restricted()
      .delete(`/events/${eventId}/ticketing/image/${kind}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async previewPdf(
    eventId: string | number,
    typeId?: number,
    lang = 'fr'
  ): Promise<Blob> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/preview`, {
        params: { type: typeId, lang },
        responseType: 'blob',
      })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async saveType(
    eventId: string | number,
    data: iTicketTypeInput,
    id?: number
  ): Promise<iTicketingSetup> {
    const c = this.restricted();
    const req = id
      ? c.put(`/events/${eventId}/ticketing/types/${id}`, data)
      : c.post(`/events/${eventId}/ticketing/types`, data);
    return await req
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async deleteType(
    eventId: string | number,
    id: number
  ): Promise<iTicketingSetup> {
    return await this.restricted()
      .delete(`/events/${eventId}/ticketing/types/${id}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async savePromo(
    eventId: string | number,
    data: iPromoInput,
    id?: number
  ): Promise<iTicketingSetup> {
    const c = this.restricted();
    const req = id
      ? c.put(`/events/${eventId}/ticketing/promo-codes/${id}`, data)
      : c.post(`/events/${eventId}/ticketing/promo-codes`, data);
    return await req
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async deletePromo(
    eventId: string | number,
    id: number
  ): Promise<iTicketingSetup> {
    return await this.restricted()
      .delete(`/events/${eventId}/ticketing/promo-codes/${id}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- tickets
  public async getTickets(
    eventId: string | number,
    params: Record<string, string | number | undefined>
  ): Promise<iTicketsPage> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/tickets`, { params })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async invite(
    eventId: string | number,
    data: Record<string, unknown>
  ): Promise<{ id: number; name: string }> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/tickets`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async inviteBatch(
    eventId: string | number,
    data: {
      ticket_type_id: number;
      send: boolean;
      rows: Array<Record<string, unknown>>;
    }
  ): Promise<{ created: number; skipped: Array<number | string> }> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/tickets/batch`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async getExhibitors(
    eventId: string | number,
    ticketTypeId: number
  ): Promise<{ exhibitors: iExhibitorCandidate[] }> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/exhibitors`, {
        params: { ticket_type_id: ticketTypeId },
      })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async updateTicket(
    eventId: string | number,
    ticketId: number,
    data: Record<string, unknown>
  ): Promise<{ status: string }> {
    return await this.restricted()
      .put(`/events/${eventId}/ticketing/tickets/${ticketId}`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async resend(
    eventId: string | number,
    ticketId: number
  ): Promise<{ message: string }> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/tickets/${ticketId}/resend`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async exportCsv(
    eventId: string | number,
    params: Record<string, string | number | undefined>
  ): Promise<Blob> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/tickets/export`, {
        params,
        responseType: 'blob',
      })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async getStats(eventId: string | number): Promise<iTicketingStats> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/stats`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- scan
  public async getScanEvents(): Promise<{ events: Array<iScanInfo['event']> }> {
    return await this.restricted()
      .get('/ticketing/scan/events')
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async getScanInfo(eventId: string | number): Promise<iScanInfo> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/scan`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Raw call (status codes needed to tell "server refused" from "no network"). */
  public async scanRequest(
    eventId: string | number,
    code: string,
    device: string
  ): Promise<{ data: iScanResult }> {
    return await this.restricted().post(
      `/events/${eventId}/ticketing/scan`,
      { code, device },
      { timeout: 6000 }
    );
  }

  public async syncScans(
    eventId: string | number,
    scans: Array<{ code: string; at: string }>,
    device: string
  ): Promise<{ synced: number; counters: iScanInfo['counters'] }> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/scan/sync`, { scans, device })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async getOfflineList(eventId: string | number): Promise<iOfflineList> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/scan/offline`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async lookup(
    eventId: string | number,
    q: string
  ): Promise<{
    results: Array<NonNullable<iScanResult['ticket']> & { raw_code: string }>;
  }> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/scan/lookup`, { params: { q } })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- checkpoints (set-up by admins and moderators)
  public async getCheckpoints(
    eventId: string | number
  ): Promise<iCheckpointList> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/checkpoints`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async saveCheckpoint(
    eventId: string | number,
    data: iCheckpointInput,
    id?: number
  ): Promise<iCheckpoint> {
    const c = this.restricted();
    const url = `/events/${eventId}/ticketing/checkpoints`;
    return await (id ? c.put(`${url}/${id}`, data) : c.post(url, data))
      .then((r) => r.data.checkpoint)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async revokeCheckpoint(
    eventId: string | number,
    id: number,
    revoked: boolean
  ): Promise<iCheckpoint> {
    return await this.restricted()
      .put(`/events/${eventId}/ticketing/checkpoints/${id}/revoke`, { revoked })
      .then((r) => r.data.checkpoint)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async regenerateCheckpoint(
    eventId: string | number,
    id: number
  ): Promise<iCheckpoint> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/checkpoints/${id}/regenerate`)
      .then((r) => r.data.checkpoint)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async deleteCheckpoint(
    eventId: string | number,
    id: number
  ): Promise<{ deleted: boolean }> {
    return await this.restricted()
      .delete(`/events/${eventId}/ticketing/checkpoints/${id}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- checkpoint scanner (secret URL, optional PIN, no EM account)
  private checkpointClient(session: string | null): AxiosInstance {
    const c = this.public();
    if (session) c.defaults.headers.common['X-Checkpoint-Session'] = session;
    return c;
  }

  public async getCheckpointInfo(
    token: string,
    session: string | null
  ): Promise<iCheckpointScanInfo> {
    return await this.checkpointClient(session)
      .get(`/ticketing/checkpoints/${token}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async unlockCheckpoint(
    token: string,
    pin: string
  ): Promise<{ session: string | null }> {
    return await this.public()
      .post(`/ticketing/checkpoints/${token}/unlock`, { pin })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Raw call, like scanRequest. */
  public async checkpointScanRequest(
    token: string,
    session: string | null,
    code: string,
    device: string
  ): Promise<{ data: iScanResult }> {
    return await this.checkpointClient(session).post(
      `/ticketing/checkpoints/${token}/scan`,
      { code, device },
      { timeout: 6000 }
    );
  }

  public async checkpointSync(
    token: string,
    session: string | null,
    scans: Array<{ code: string; at: string }>,
    device: string
  ): Promise<{ synced: number; counters: iScanInfo['counters'] }> {
    return await this.checkpointClient(session)
      .post(`/ticketing/checkpoints/${token}/sync`, { scans, device })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async checkpointOfflineList(
    token: string,
    session: string | null
  ): Promise<iOfflineList> {
    return await this.checkpointClient(session)
      .get(`/ticketing/checkpoints/${token}/offline`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- exhibitor codes (organiser)
  public async getExhibitorPasses(
    eventId: string | number
  ): Promise<iExhibitorPassList> {
    return await this.restricted()
      .get(`/events/${eventId}/ticketing/exhibitor-passes`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async saveExhibitorPassSettings(
    eventId: string | number,
    data: { exhibitor_quota?: number; exhibitor_type_id?: number | null }
  ): Promise<iExhibitorPassList> {
    return await this.restricted()
      .put(`/events/${eventId}/ticketing/exhibitor-passes/settings`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async updateExhibitorPass(
    eventId: string | number,
    id: number,
    data: { quota?: number | null; contact_email?: string | null }
  ): Promise<iExhibitorPassList> {
    return await this.restricted()
      .put(`/events/${eventId}/ticketing/exhibitor-passes/${id}`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async regenerateExhibitorPass(
    eventId: string | number,
    id: number
  ): Promise<iExhibitorPassList> {
    return await this.restricted()
      .post(`/events/${eventId}/ticketing/exhibitor-passes/${id}/regenerate`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  /** Without ids: every exhibitor not contacted yet. */
  public async sendExhibitorPasses(
    eventId: string | number,
    ids?: number[]
  ): Promise<{ sent: number; not_sent: string[] }> {
    return await this.restricted()
      .post(
        `/events/${eventId}/ticketing/exhibitor-passes/send`,
        ids ? { ids } : {}
      )
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- exhibitor page (public, by code)
  public async getExhibitorPassPage(code: string): Promise<iExhibitorPassPage> {
    return await this.public()
      .get(`/ticketing/exhibitor-pass/${code}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async addExhibitorBadges(
    code: string,
    rows: Array<{ firstname: string; lastname: string; job_title: string }>,
    photos: Array<File | null>
  ): Promise<iExhibitorPassPage> {
    const form = new FormData();
    rows.forEach((r, i) => {
      form.append(`rows[${i}][firstname]`, r.firstname);
      form.append(`rows[${i}][lastname]`, r.lastname);
      form.append(`rows[${i}][job_title]`, r.job_title);
      const photo = photos[i];
      if (photo) form.append(`photos[${i}]`, photo);
    });
    return await this.public()
      .post(`/ticketing/exhibitor-pass/${code}/badges`, form)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async updateExhibitorBadge(
    code: string,
    id: number,
    data: {
      firstname?: string;
      lastname?: string;
      job_title?: string;
      photo?: File;
      remove_photo?: boolean;
    }
  ): Promise<iExhibitorPassPage> {
    const form = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (v === undefined) return;
      form.append(k, typeof v === 'boolean' ? (v ? '1' : '0') : v);
    });
    return await this.public()
      .post(`/ticketing/exhibitor-pass/${code}/badges/${id}`, form)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async cancelExhibitorBadge(
    code: string,
    id: number
  ): Promise<iExhibitorPassPage> {
    return await this.public()
      .delete(`/ticketing/exhibitor-pass/${code}/badges/${id}`)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  // ---- public (visitors)
  public async getPublic(
    company: string,
    slug: string,
    locale: string
  ): Promise<iPublicTicketing> {
    return await this.public()
      .get(`/${company}/ticketing/events/${slug}`, { params: { locale } })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async checkPromo(
    company: string,
    slug: string,
    code: string,
    ticketTypeId: number,
    locale: string
  ): Promise<{ valid: boolean; discount_cents: number; total_cents: number }> {
    return await this.public()
      .post(`/${company}/ticketing/events/${slug}/promo`, {
        code,
        ticket_type_id: ticketTypeId,
        locale,
      })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async register(
    company: string,
    slug: string,
    data: Record<string, unknown>
  ): Promise<iRegisterResponse> {
    return await this.public()
      .post(`/${company}/ticketing/events/${slug}/register`, data)
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }

  public async getOrder(
    company: string,
    reference: string,
    key: string,
    sessionId?: string | null
  ): Promise<iPublicOrder> {
    return await this.public()
      .get(`/${company}/ticketing/orders/${reference}`, {
        params: { k: key, session_id: sessionId ?? undefined },
      })
      .then((r) => r.data)
      .catch((e) => {
        throw apiError(e);
      });
  }
}

export type iTicketingRepository = TicketingRepository;
